import { parse, stringify } from 'query-string';

const hasWindow = typeof window !== 'undefined';
const storageKey = '__DT_SOURCE';
const sessionIdKey = '__DT_SOURCE_SESSION_ID';
const lastClickElementKey = '__last_clck_ele';
const isFromJsOpenKey = '__is_from_js_open';
const mergeEleParamsToSource = (sourceEl) => {
    const source = Object.assign({}, getDtSource());
    source[lastClickElementKey] = sourceEl;
    source[isFromJsOpenKey] = true;
    return source;
};
/**
 * 在当前session中打开，并且把参数带过去
 * @param url
 * @param sourceEl - 带过去的参数
 */
const openInCurrentSession = (url, sourceEl = {}) => {
    setLastClickEle(sourceEl);
    window.open(url, '_self');
    return null;
};
/**
 * 在新的session中打开，并且把参数带过去
 * @param url
 * @param target - open的target，不能为_self
 * @param sourceEl - 带过去的参数
 */
const openInNewSession = (url, target, sourceEl = {}) => {
    if (target === '_self') {
        return null;
    }
    const dtSource = mergeEleParamsToSource(sourceEl);
    const targetParams = {
        target,
        dtSource,
    };
    const targetName = JSON.stringify(targetParams);
    // 这里要移除一下，认为是不同的sessionId了
    const newWindow = window.open(url, targetName);
    if (newWindow) {
        try {
            // 跨域可能会报错
            newWindow.sessionStorage.removeItem(sessionIdKey);
            newWindow.sessionStorage.removeItem(storageKey);
        }
        catch (err) {
            // doNothing
        }
    }
    return newWindow;
};
function openLink(url, target, dtSource) {
    if (target !== '_self') {
        return openInNewSession(url, target, dtSource);
    }
    else {
        return openInCurrentSession(url, dtSource);
    }
}
function getDtSource() {
    // 如果已经有了window.dtSource，则直接返回，否则到sessionStorage里取
    if (window.dtSource) {
        return window.dtSource;
    }
    // 将 sessionStorage 的缓存到 window.dtSource 里
    const storagedSource = window.sessionStorage.getItem(storageKey);
    if (storagedSource) {
        try {
            window.dtSource = JSON.parse(storagedSource);
        }
        catch (err) {
            console.warn('session storaged dtSource is not a valid object', err);
        }
    }
    return window.dtSource || null;
}
function merge(target, source) {
    for (const key in source) {
        if (source[key] !== undefined) {
            target[key] = source[key];
        }
    }
}
/**
 * 清空 dtSource 参数
 */
function clearDtSource() {
    window.dtSource = {};
    setDtSource({});
}
/**
 * 设置dtSource参数
 */
function setDtSource(sourceObj, w = window) {
    if (!w.dtSource) {
        console.error(`must call ${initDtSource.name} first`);
        return;
    }
    merge(w.dtSource, sourceObj);
    w.sessionStorage.setItem(storageKey, JSON.stringify(w.dtSource));
}
/**
 * 更新当前的页面的 cur_pg
 */
function setCurrentPage(pageObj) {
    setDtSource({ cur_pg: pageObj });
}
/**
 * 用于单页面应用程序，页面在路由的时候，设置点击的元素参数
 * @example
 *
 * function route() {
 *   setLastClickEle({ xxxx });
 *   router.push('/xxxx');
 * }
 *
 */
function setLastClickEle(sourceEl) {
    setDtSource(mergeEleParamsToSource(sourceEl));
}
/**
 * 获取页面来源参数
 * @param curPg - 是否返回 cur_pg
 * @returns 页面来源的对象，可以直接调用大同的api设置
 *
 * @example
 * ```typescript
 * reporter.setPage('page_detail', {
 *   ...getDtPgParams(),
 * })
 * ```
 */
function getDtPgParams(curPg) {
    const { cre_pg, cur_pg, ref_pg } = getDtSource() || {};
    return curPg
        ? {
            cur_pg,
            ref_pg,
            cre_pg,
        }
        : {
            cre_pg,
            ref_pg,
        };
}
let getSessionId;
/**
 * 初始化，可用于单页应用程序，在进入组件时调用initDtSource
 *
 * @param dtSource - 初始化参数
 */
function initDtSource(dtSource) {
    var _a;
    // 优先判断window.name里有没有dtSource的数据，有的话存入sessionStorage
    if (window.name) {
        try {
            const targetParams = JSON.parse(window.name);
            if (targetParams.dtSource) {
                window.sessionStorage.setItem(storageKey, JSON.stringify(targetParams.dtSource));
            }
            window.name = targetParams.target === '_blank' ? '' : targetParams.target || '';
        }
        catch (err) { }
    }
    // 如果有window.dtSource，则设置到sessionStorage里
    // 用于刷新页面时保留dtSource参数
    const source = getDtSource() || {};
    window.dtSource = source;
    const lastSessionId = sessionStorage.getItem(sessionIdKey);
    const sessionId = ((_a = dtSource === null || dtSource === void 0 ? void 0 : dtSource.getSessionId) === null || _a === void 0 ? void 0 : _a.call(dtSource, false)) || window.location.href;
    const isSameSession = lastSessionId === sessionId;
    getSessionId = dtSource === null || dtSource === void 0 ? void 0 : dtSource.getSessionId;
    sessionStorage.setItem(sessionIdKey, sessionId);
    const obj = {};
    // 如果不是在同一个session，则判定为页面跳转
    // 然后把ref_pg设置为之前的cur_pg
    if (!isSameSession) {
        // 设置为之前的cre_pg，如果没有设置则为refPg，如果都没有则为默认参数
        const isFromJsOpen = source[isFromJsOpenKey];
        // 读取到参数后设置为false
        source[isFromJsOpenKey] = false;
        // 程序打开的页面
        if (isFromJsOpen) {
            const refPg = source.cur_pg;
            obj.ref_pg = refPg;
            if (obj.ref_pg) {
                obj.ref_pg.last_clck_ele = source[lastClickElementKey];
            }
            obj.cre_pg = source.cre_pg || refPg || (dtSource === null || dtSource === void 0 ? void 0 : dtSource.default_cre_pg);
        }
        else {
            // 用户手动输入的页面
            clearDtSource();
            obj.cre_pg = dtSource === null || dtSource === void 0 ? void 0 : dtSource.default_cre_pg;
        }
    }
    // 设置当前页面
    obj.cur_pg = dtSource === null || dtSource === void 0 ? void 0 : dtSource.cur_pg;
    setDtSource(obj);
    registerEventOnce();
}
let registered = false;
function registerEventOnce() {
    if (registered) {
        return;
    }
    registered = true;
    hasWindow &&
        window.addEventListener('beforeunload', () => {
            const sessionId = (getSessionId === null || getSessionId === void 0 ? void 0 : getSessionId(true)) || window.location.href;
            sessionStorage.setItem(sessionIdKey, sessionId);
        });
    const findElementHasAttr = (target, attr, stopAttr) => {
        var _a, _b;
        let t = target;
        while (t) {
            if ((_a = t.hasAttribute) === null || _a === void 0 ? void 0 : _a.call(t, stopAttr)) {
                return null;
            }
            if ((_b = t.hasAttribute) === null || _b === void 0 ? void 0 : _b.call(t, attr)) {
                return t;
            }
            t = t.parentElement;
        }
        return null;
    };
    // 拦截a标签的click事件，使用window.open打开页面，同时将要传递的值赋值给新窗口的window.dtSource
    hasWindow &&
        document.body.addEventListener('click', (event) => {
            const target = event.target;
            if (target instanceof Element) {
                const dtSourceAttr = 'dt-source-el';
                // 找到含有 dtSourceAttr 的元素
                const el = findElementHasAttr(target, dtSourceAttr, 'dt-source-ignore');
                if (el) {
                    const openWithLastClickElement = (sourceInfo) => {
                        // 如果是a标签
                        if (el instanceof HTMLAnchorElement) {
                            if (el.target === '_self' || !el.target) {
                                // 没办法获取到打开的window对象，因为已经打开了...
                                // 存到sessionStorage
                                openInCurrentSession(el.href, sourceInfo);
                            }
                            else {
                                // 如果是在新的页面打开
                                // 获取到window后给dtSource赋值
                                openInNewSession(el.href, el.target, sourceInfo);
                            }
                            // 阻止a标签默认事件
                            event.preventDefault();
                        }
                        else {
                            // 其他标签
                            setLastClickEle(sourceInfo);
                        }
                    };
                    // 如果它的值不是 auto，则为手动设置的对象
                    if (el.getAttribute(dtSourceAttr) !== 'auto') {
                        const lastClickEle = el.getAttribute(dtSourceAttr) || '';
                        // 解析出来
                        const elParams = parse(lastClickEle);
                        openWithLastClickElement(elParams);
                    }
                    else {
                        // 如果为 auto，则自动读取大同的参数
                        const dtParams = el.getAttribute('dt-params') || '';
                        const dtEid = el.getAttribute('dt-eid');
                        const paramsObj = parse(dtParams);
                        dtEid && (paramsObj.eid = dtEid);
                        openWithLastClickElement(paramsObj);
                    }
                }
            }
        }, 
        // 优先捕获，比vue-router提前
        { capture: true });
}
/**
 * 仅提供类型提示，做一次JSON.stringify的转换，用于在模版a标签中使用 dt-source-el 参数
 * @example
 * ```vue
 * <a href="/foo" target="_blank" :dt-source-el="toDtSourceElParams({ eid: 'bar' })">
 * 打开页面
 * </a>
 * ```
 */
function toDtSourceElParams(sourceObj) {
    return stringify(sourceObj);
}
/**
 * 获取来源页面，用于传递给大同上报
 * @example
 * ```typescript
 * reporter.setPage({
 *   ...getSourcePages()
 * })
 * ```
 */
function getSourcePages() {
    const { cre_pg, ref_pg } = getDtSource() || {};
    return {
        cre_pg,
        ref_pg,
    };
}

var DtSource = /*#__PURE__*/Object.freeze({
    __proto__: null,
    openLink: openLink,
    getDtSource: getDtSource,
    clearDtSource: clearDtSource,
    setDtSource: setDtSource,
    setCurrentPage: setCurrentPage,
    setLastClickEle: setLastClickEle,
    getDtPgParams: getDtPgParams,
    initDtSource: initDtSource,
    toDtSourceElParams: toDtSourceElParams,
    getSourcePages: getSourcePages
});

export { clearDtSource, DtSource as default, getDtPgParams, getDtSource, getSourcePages, initDtSource, openLink, setCurrentPage, setDtSource, setLastClickEle, toDtSourceElParams };
